
import { Component, Vue } from "vue-property-decorator";
import CanvasCourseMappingApiService from "@/services/CanvasCourseMappingApiService";
import CanvasSyncSettings from "@/types/Canvas/CanvasSyncSettings";
import SyncResult from "@/types/Canvas/SyncResult";
import VMSTextField from "@/components/fields/VMSTextField.vue";

const MIN_HOURS = 1;
const MAX_HOURS = 8760;

@Component({
  components: { VMSTextField }
})
export default class CanvasSyncConfigPanel extends Vue {
  api = new CanvasCourseMappingApiService();

  settings: CanvasSyncSettings = new CanvasSyncSettings();
  lookbackInput: string | number = 25;
  savingSettings: boolean = false;

  syncing: boolean = false;
  lastResult: SyncResult | null = null;

  snackbar: { show: boolean; color: string; message: string } = {
    show: false,
    color: "success",
    message: ""
  };

  lookbackRules: Function[] = [
    (v: string | number) => {
      const n = Number(v);
      return (
        (Number.isInteger(n) && n >= MIN_HOURS && n <= MAX_HOURS) ||
        (this.$t("LookbackHoursInvalid") as string)
      );
    }
  ];

  courseHeaders: object[] = [
    { text: this.$root.$t("CanvasCourseId"), value: "CanvasCourseId" },
    { text: this.$root.$t("Enrollments"), value: "Enrollments" },
    { text: this.$root.$t("Refreshed"), value: "Refreshed" },
    { text: this.$root.$t("AlreadyCompleted"), value: "AlreadyCompleted" },
    { text: this.$root.$t("InProgress"), value: "InProgress" },
    {
      text: this.$root.$t("CompletedOutsideWindow"),
      value: "CompletedOutsideWindow"
    },
    { text: this.$root.$t("SkippedNoUser"), value: "SkippedNoUser" },
    { text: this.$root.$t("ErrorMessage"), value: "Error" }
  ];

  outcomeHeaders: object[] = [
    { text: this.$root.$t("Status"), value: "Status" },
    { text: this.$root.$t("CanvasCourseId"), value: "CanvasCourseId" },
    { text: this.$root.$t("Message"), value: "Message" }
  ];

  mounted() {
    this.loadSettings();
  }

  async loadSettings() {
    const resp = await this.api.GetSyncSettings();
    if (resp.Success && resp.Value) {
      this.settings = resp.Value;
      this.lookbackInput = resp.Value.LookbackHours;
    }
  }

  async saveSettings() {
    const n = Number(this.lookbackInput);
    if (!Number.isInteger(n) || n < MIN_HOURS || n > MAX_HOURS) {
      this.showSnack("error", this.$t("LookbackHoursInvalid").toString());
      return;
    }
    this.savingSettings = true;
    try {
      this.settings.LookbackHours = n;
      const resp = await this.api.UpdateSyncSettings(this.settings);
      if (resp.Success) {
        this.showSnack("success", this.$t("SettingsSaved").toString());
      } else {
        this.showSnack(
          "error",
          resp.Message || this.$t("LookbackHoursInvalid").toString()
        );
      }
    } finally {
      this.savingSettings = false;
    }
  }

  async syncNow() {
    this.syncing = true;
    try {
      const resp = await this.api.SyncNowAll();
      if (resp.Success && resp.Value) {
        this.lastResult = resp.Value;
        this.showSnack(
          resp.Value.Failed > 0 ? "warning" : "success",
          this.$t("SyncResultMessage", {
            refreshed: resp.Value.Refreshed,
            logged: resp.Value.Logged,
            warned: resp.Value.Warned,
            failed: resp.Value.Failed
          }).toString()
        );
        this.$emit("synced");
      } else {
        this.showSnack(
          "error",
          resp.Message || this.$t("SyncFailed").toString()
        );
      }
    } finally {
      this.syncing = false;
    }
  }

  formatDate(iso: string | undefined): string {
    if (!iso) {
      return "";
    }
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  }

  showSnack(color: string, message: string) {
    this.snackbar.color = color;
    this.snackbar.message = message;
    this.snackbar.show = true;
  }
}
